import type { MapElementRequestAPI } from '../sources/graph';
import { getMapElement, saveMapElement } from '../sources/graph';
import type { MapElement, Outcome } from '../types/graph';
import { isEmptyGuid } from './guid';

/**
 * Checks if an approval element is missing the Approve or Deny outcome
 * @param mapElement The map element to check
 * @returns Object indicating which outcomes are missing
 */
export const getMissingApprovalOutcomes = (
    mapElement: MapElement | undefined,
): { missingApprove: boolean; missingDeny: boolean } => {
    if (mapElement?.elementType !== 'approval') {
        return { missingApprove: false, missingDeny: false };
    }

    // Check by ID if approval object exists
    let hasApprovedOutcome = false;
    let hasDeniedOutcome = false;

    if (mapElement.approval) {
        hasApprovedOutcome = mapElement.approval.approvedOutcomeId != null;
        hasDeniedOutcome = mapElement.approval.deniedOutcomeId != null;
    }

    // If IDs aren't set (or approval object doesn't exist), check by name as fallback
    // This handles existing outcomes that were created before the IDs were added, or when approval is null/undefined
    // We check by name only because order might not be set on legacy outcomes
    if (!hasApprovedOutcome && mapElement.outcomes) {
        hasApprovedOutcome = mapElement.outcomes.some((o) => o.developerName === 'Approve');
    }

    if (!hasDeniedOutcome && mapElement.outcomes) {
        hasDeniedOutcome = mapElement.outcomes.some((o) => o.developerName === 'Deny');
    }

    return {
        missingApprove: !hasApprovedOutcome,
        missingDeny: !hasDeniedOutcome,
    };
};

/**
 * Determines if a new outcome should be auto-assigned as Approve or Deny
 * @param mapElement The map element
 * @returns 'approve', 'deny', or null
 */
export const getAutoAssignmentType = (
    mapElement: MapElement | undefined,
): 'approve' | 'deny' | null => {
    const { missingApprove, missingDeny } = getMissingApprovalOutcomes(mapElement);

    if (missingApprove) {
        return 'approve';
    }
    if (missingDeny) {
        return 'deny';
    }
    return null;
};

/**
 * Applies approval outcome constraints to an outcome
 * @param outcome The outcome to modify
 * @param type 'approve' or 'deny'
 * @returns Modified outcome with constraints applied
 */
export const applyApprovalOutcomeConstraints = (
    outcome: Partial<Outcome>,
    type: 'approve' | 'deny',
): Partial<Outcome> => {
    return {
        ...outcome,
        developerName: type === 'approve' ? 'Approve' : 'Deny',
        label: type === 'approve' ? 'Approve' : 'Deny',
        order: type === 'approve' ? 0 : 1,
    };
};

/**
 * Checks if an outcome is a special approval outcome (Approve or Deny)
 * @param outcome The outcome to check
 * @param mapElement The map element
 * @returns Object indicating if it's Approve or Deny outcome
 */
export const getApprovalOutcomeType = (
    outcome: Partial<Outcome> | null,
    mapElement: MapElement | undefined,
): { isApprove: boolean; isDeny: boolean } => {
    if (!outcome || !mapElement || mapElement.elementType !== 'approval') {
        return { isApprove: false, isDeny: false };
    }

    // First check by ID if available
    let isApprove = outcome.id === mapElement.approval?.approvedOutcomeId;
    let isDeny = outcome.id === mapElement.approval?.deniedOutcomeId;

    // If not matched by ID, check by name as fallback
    // This handles existing outcomes that were created before the IDs were added
    // We check by name only because order might not be set on legacy outcomes
    if (!isApprove && !isDeny && outcome.id) {
        if (outcome.developerName === 'Approve') {
            isApprove = true;
        } else if (outcome.developerName === 'Deny') {
            isDeny = true;
        }
    }

    return { isApprove, isDeny };
};

/**
 * Updates the approval object with new outcome assignments
 * @param mapElement The map element to update
 * @param outcomeId The outcome ID to assign
 * @param type 'approve' or 'deny'
 * @returns Updated map element
 */
export const updateApprovalOutcomeAssignment = (
    mapElement: MapElement,
    outcomeId: string,
    type: 'approve' | 'deny',
): MapElement => {
    // If approval object doesn't exist, create it with default values
    const approval = mapElement.approval || {
        approvalDetails: [],
        approvalComparisonType: 'All' as const,
        denialComparisonType: 'All' as const,
        approvedOutcomeId: null,
        deniedOutcomeId: null,
    };

    const updated = {
        ...mapElement,
        approval: {
            ...approval,
            ...(type === 'approve'
                ? { approvedOutcomeId: outcomeId }
                : { deniedOutcomeId: outcomeId }),
        },
    };

    // Ensure outcomes is always an array
    if (updated.outcomes === null || updated.outcomes === undefined) {
        updated.outcomes = [];
    }

    return updated;
};

/**
 * Clears approval outcome assignments when an outcome is deleted
 * @param mapElement The map element to update
 * @param outcomeId The outcome ID that was deleted
 * @returns Updated map element
 */
export const clearApprovalOutcomeAssignment = (
    mapElement: MapElement,
    outcomeId: string,
): MapElement => {
    if (!mapElement.approval) {
        return mapElement;
    }

    const updates: { approvedOutcomeId?: null; deniedOutcomeId?: null } = {};

    if (mapElement.approval.approvedOutcomeId === outcomeId) {
        updates.approvedOutcomeId = null;
    }

    if (mapElement.approval.deniedOutcomeId === outcomeId) {
        updates.deniedOutcomeId = null;
    }

    if (Object.keys(updates).length === 0) {
        return mapElement;
    }

    const updated = {
        ...mapElement,
        approval: {
            ...mapElement.approval,
            ...updates,
        },
    };

    // Ensure outcomes is always an array
    if (updated.outcomes === null || updated.outcomes === undefined) {
        updated.outcomes = [];
    }

    return updated;
};

/**
 * Initializes approval outcome IDs based on existing outcomes with correct names/orders
 * @param mapElement The map element to update
 * @returns Updated map element with IDs set
 */
export const initializeApprovalOutcomeIds = (mapElement: MapElement): MapElement => {
    if (mapElement.elementType !== 'approval' || !mapElement.outcomes) {
        return mapElement;
    }

    // Check if IDs are already set (treating empty GUIDs as not set)
    const currentApprovedId = mapElement.approval?.approvedOutcomeId;
    const currentDeniedId = mapElement.approval?.deniedOutcomeId;
    const hasApprovedId = currentApprovedId != null && !isEmptyGuid(currentApprovedId);
    const hasDeniedId = currentDeniedId != null && !isEmptyGuid(currentDeniedId);

    // If both IDs are already properly set, no need to initialize
    if (hasApprovedId && hasDeniedId) {
        return mapElement;
    }

    const approveOutcome = mapElement.outcomes.find(
        (o) => o.developerName === 'Approve' && o.order === 0,
    );
    const denyOutcome = mapElement.outcomes.find(
        (o) => o.developerName === 'Deny' && o.order === 1,
    );

    if (!approveOutcome && !denyOutcome) {
        return mapElement;
    }

    const approval = mapElement.approval || {
        approvalDetails: [],
        approvalComparisonType: 'All' as const,
        denialComparisonType: 'All' as const,
        approvedOutcomeId: null,
        deniedOutcomeId: null,
    };

    // Get the appropriate ID, filtering out empty GUIDs
    const newApprovedId = hasApprovedId ? currentApprovedId : (approveOutcome?.id ?? null);
    const newDeniedId = hasDeniedId ? currentDeniedId : (denyOutcome?.id ?? null);

    return {
        ...mapElement,
        approval: {
            ...approval,
            approvedOutcomeId: newApprovedId,
            deniedOutcomeId: newDeniedId,
        },
    };
};

/**
 * Clears deleted outcome references from all approval elements in the flow
 * @param mapElements Array of map elements (can be shallow copies)
 * @param deletedOutcomeIds Array of outcome IDs that are being deleted
 * @param flowId The flow ID
 * @returns Promise that resolves when all approval elements are updated
 */
export const clearDeletedOutcomeReferencesFromApprovals = async (
    mapElements: MapElement[],
    deletedOutcomeIds: string[],
    flowId: string,
): Promise<void> => {
    if (deletedOutcomeIds.length === 0) {
        return;
    }

    // Find all approval element IDs (works even with shallow copies from state)
    const approvalElementIds = mapElements
        .filter((el) => el.elementType === 'approval')
        .map((el) => el.id);

    // Fetch full approval elements to check and update them
    for (const approvalElementId of approvalElementIds) {
        const fullApprovalElement = await getMapElement(approvalElementId, flowId);

        // Check if this approval element references any of the deleted outcomes
        const referencesDeletedOutcome =
            fullApprovalElement.approval &&
            (deletedOutcomeIds.includes(fullApprovalElement.approval.approvedOutcomeId ?? '') ||
                deletedOutcomeIds.includes(fullApprovalElement.approval.deniedOutcomeId ?? ''));

        if (referencesDeletedOutcome) {
            // Clear the deleted outcome references
            let updatedApproval: MapElement = fullApprovalElement;
            for (const outcomeId of deletedOutcomeIds) {
                updatedApproval = clearApprovalOutcomeAssignment(updatedApproval, outcomeId);
            }
            await saveMapElement(updatedApproval as MapElementRequestAPI, flowId);
        }
    }
};
